import React, { useState } from 'react';
import { getSessionHeaders } from '../lib/serverSession';
import ProfileImageCropper from './ProfileImageCropper';
import { SignedProfileImage } from './SignedProfileImage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ProfilePictureUploadProps {
  currentImageUrl?: string;
  userId: number;
  onImageUpdate: (newImageUrl: string) => void;
  size?: 'small' | 'medium' | 'large';
  editable?: boolean;
  userInitials?: string;
  userName?: string;
  showSaveButton?: boolean;
  autoSave?: boolean;
}

export const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentImageUrl,
  userId,
  onImageUpdate,
  size = 'medium',
  editable = true,
  userInitials = 'U',
  userName = 'User',
  showSaveButton = true,
  autoSave = false
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [, setHasUnsavedChanges] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const sizeClasses = {
    small: { container: '48px', text: '18px' },
    medium: { container: '80px', text: '28px' },
    large: { container: '120px', text: '40px' }
  };

  const currentSize = sizeClasses[size];

  /** Called by ProfileImageCropper with an optimized 500×500 JPEG blob */
  const handleCropped = async (blob: Blob) => {
    setError(null);
    const previewUrl = URL.createObjectURL(blob);
    setPendingImageUrl(previewUrl);
    setSelectedFile(new File([blob], 'profile.jpg', { type: 'image/jpeg' }));
    setHasUnsavedChanges(true);

    if (autoSave) {
      await uploadBlob(blob);
    }
  };

  const handleSave = async () => {
    if (!selectedFile) return;
    await uploadBlob(selectedFile);
  };

  const uploadBlob = async (blob: Blob) => {
    setSaving(true);
    setError(null);

    try {
      // Convert optimized blob to base64 (~50-150KB for 500×500 JPEG)
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      console.log('📤 Uploading profile picture via API for user ID:', userId);

      // Upload via backend API which uses service role (bypasses storage RLS)
      const response = await fetch(`${API_BASE_URL}/api/user/profile-completion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'upload_profile_picture',
          data: {
            fileBase64: base64Data,
            fileName: 'profile.jpg',
            fileType: 'image/jpeg'
          }
        })
      });

      // Safe JSON parse — large payloads can hit Vercel body limits, returning non-JSON
      let result: any;
      try {
        result = await response.json();
      } catch {
        throw new Error('Server returned an invalid response. The image may be too large — please try a smaller photo.');
      }
      if (!response.ok) {
        console.error('❌ Upload error:', result.error);
        throw new Error(`Upload failed: ${result.error || 'Server error'}`);
      }

      const publicUrl = result.data?.profilePictureUrl;
      if (!publicUrl) {
        throw new Error('No profile picture URL returned from server');
      }

      console.log('✅ Profile picture uploaded and saved:', publicUrl);

      // Update localStorage with new profile picture URL and timestamp
      const currentTimestamp = new Date().toISOString();
      const storedUser = localStorage.getItem('aureus_user');
      if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
        try {
          const userData = JSON.parse(storedUser);
          userData.profile_image_url = publicUrl;
          userData.updated_at = currentTimestamp;
          // CRITICAL FIX: Also update database_user if it exists
          if (userData.database_user) {
            userData.database_user.profile_image_url = publicUrl;
            userData.database_user.updated_at = currentTimestamp;
          }
          if (userData.user_metadata) {
            userData.user_metadata.profile_image_url = publicUrl;
          }
          localStorage.setItem('aureus_user', JSON.stringify(userData));
          console.log('✅ Profile picture URL updated in localStorage (root + database_user) with timestamp');
        } catch (err) {
          console.error('Failed to parse aureus_user from localStorage:', err);
        }
      }

      onImageUpdate(publicUrl);

      // Clear selected file and pending state
      setSelectedFile(null);
      setPendingImageUrl(null);
      setHasUnsavedChanges(false);
      setSuccess('Profile picture updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (err: any) {
      console.error('Profile picture upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setSaving(false);
    }
  };

  // handleClick no longer needed — ProfileImageCropper handles file selection



  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Profile Picture Container — wrapped in cropper */}
      <ProfileImageCropper
        onCropped={handleCropped}
        disabled={!editable || saving}
        trigger={
      <div
        style={{
          width: currentSize.container,
          height: currentSize.container,
          borderRadius: '50%',
          overflow: 'hidden',
          cursor: editable ? 'pointer' : 'default',
          position: 'relative',
          background: currentImageUrl 
            ? 'transparent' 
            : 'linear-gradient(135deg, #FFD700 0%, #E6C200 100%)',
          border: '3px solid rgba(255, 215, 0, 0.3)',
          boxShadow: '0 0 18px rgba(255, 215, 0, 0.3)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (editable) {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 0 24px rgba(255, 215, 0, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (editable) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 0 18px rgba(255, 215, 0, 0.3)';
          }
        }}
      >
        {pendingImageUrl ? (
          <img
            src={pendingImageUrl}
            alt={`${userName} Profile Picture`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <SignedProfileImage
            rawUrl={currentImageUrl}
            alt={`${userName} Profile Picture`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            fallback={
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000000',
            fontSize: currentSize.text,
            fontWeight: 'bold'
          }}>
            {userInitials}
          </div>
            }
          />
        )}

        {/* Upload Overlay */}
        {editable && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            borderRadius: '50%'
          }}
          className="upload-overlay"
          >
            <span style={{ color: 'white', fontSize: '14px' }}>
              {saving ? '⏳' : '📷'}
            </span>
          </div>
        )}
      </div>
        } /* end trigger */
      />

      {/* Loading Indicator */}
      {saving && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0, 0, 0, 0.8)',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid transparent',
            borderTop: '2px solid #FFD700',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
        </div>
      )}

      {/* Save Button */}
      {showSaveButton && selectedFile && !saving && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          zIndex: 10
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: saving ? '#666' : '#FFD700',
              color: saving ? '#ccc' : '#000',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {saving ? 'Saving...' : 'Save Picture'}
          </button>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: showSaveButton && selectedFile ? '48px' : '8px',
          padding: '4px 8px',
          background: 'rgba(34, 197, 94, 0.9)',
          color: 'white',
          borderRadius: '4px',
          fontSize: '13px',
          whiteSpace: 'nowrap',
          zIndex: 10
        }}>
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: showSaveButton && selectedFile ? '48px' : (success ? '28px' : '8px'),
          padding: '4px 8px',
          background: 'rgba(239, 68, 68, 0.9)',
          color: 'white',
          borderRadius: '4px',
          fontSize: '13px',
          whiteSpace: 'nowrap',
          zIndex: 10
        }}>
          {error}
        </div>
      )}
    </div>
  );
};
